import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Star, CheckCircle, GoogleLogo, Warning, Heart } from "@phosphor-icons/react";

/**
 * Public client-facing rating page.
 * Route: `/rate/:token` — no auth. If rating >= threshold and a Google URL
 * is configured, the client is invited to leave a Google review.
 */
export default function SavRating() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { rating, should_redirect_google, google_review_url }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/sav-rating/${token}`);
        if (!alive) return;
        setState({ loading: false, error: null, data: r.data });
        if (r.data.existing_rating) setRating(r.data.existing_rating);
        if (r.data.existing_comment) setComment(r.data.existing_comment);
      } catch (err) {
        if (!alive) return;
        setState({
          loading: false,
          error: err.response?.data?.detail || "Ce lien est invalide ou expiré.",
          data: null,
        });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/sav-rating/${token}`, { rating, comment: comment.trim() || null });
      setResult(r.data);
    } catch (err) {
      setState((s) => ({ ...s, error: err.response?.data?.detail || "Erreur lors de l'envoi" }));
    } finally {
      setSubmitting(false);
    }
  };

  const trackGoogleClick = () => {
    // fire-and-forget
    axios.post(`${API}/public/sav-rating/${token}/google-click`, { clicked: true }).catch(() => {});
  };

  if (state.loading) {
    return (
      <Shell>
        <div className="animate-pulse text-white/50 text-center py-20">Chargement…</div>
      </Shell>
    );
  }

  if (state.error) {
    return (
      <Shell>
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-8 text-center space-y-3" data-testid="rating-error">
          <Warning size={40} weight="duotone" className="text-red-400 mx-auto" />
          <div className="font-display font-bold text-xl">Lien invalide</div>
          <div className="text-sm text-white/70">{state.error}</div>
        </div>
      </Shell>
    );
  }

  // -- Post-submit: thank you + optional Google review CTA -----------------
  if (result) {
    const positive = result.should_redirect_google && result.google_review_url;
    return (
      <Shell>
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-8 text-center space-y-5" data-testid="rating-thanks">
          {positive ? (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 border-2 border-emerald-500/40 flex items-center justify-center">
                <CheckCircle size={36} weight="duotone" className="text-emerald-400" />
              </div>
              <div className="font-display font-bold text-2xl">Merci pour votre note !</div>
              <div className="text-sm text-white/70 leading-relaxed">
                Nous sommes ravis. Si vous avez 30 secondes, votre avis sur <b>Google</b> aide énormément
                {" "}<b>{state.data.business_name || "notre entreprise"}</b> à se faire connaître.
              </div>
              <a
                href={result.google_review_url}
                target="_blank"
                rel="noreferrer"
                onClick={trackGoogleClick}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-gradient-to-r from-[#4285F4] via-[#EA4335] to-[#FBBC05] text-white font-semibold shadow-lg hover:opacity-95 transition-opacity"
                data-testid="google-review-btn"
              >
                <GoogleLogo size={20} weight="bold" /> Laisser un avis Google
              </a>
              <div className="text-[11px] text-white/40">Vous serez redirigé vers Google — aucun compte requis, cela prend une minute.</div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#005CFF]/15 border-2 border-[#005CFF]/40 flex items-center justify-center">
                <Heart size={34} weight="duotone" className="text-[#7CB2FF]" />
              </div>
              <div className="font-display font-bold text-2xl">Merci pour votre retour</div>
              <div className="text-sm text-white/70 leading-relaxed">
                Vos remarques nous aident à progresser. Un responsable prendra connaissance de votre message
                {" "}et pourra vous recontacter si nécessaire.
              </div>
            </>
          )}
        </div>
      </Shell>
    );
  }

  // -- Rating form --------------------------------------------------------
  const t = state.data.ticket;
  const already = state.data.already_rated;
  return (
    <Shell>
      <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 space-y-5" data-testid="rating-form">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Ticket SAV résolu</div>
          <div className="font-display font-bold text-xl">{t.title || "Intervention"}</div>
          {t.client_name && <div className="text-sm text-white/60 mt-1">{t.client_name}</div>}
        </div>

        <div className="text-sm text-white/75 leading-relaxed">
          {already
            ? "Vous avez déjà noté cette intervention — vous pouvez modifier votre commentaire ci-dessous."
            : "Merci d'avoir fait appel à nos services. Comment évaluez-vous cette intervention ?"}
        </div>

        <div className="flex justify-center gap-1.5 py-3" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = n <= (hover || rating);
            return (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                className="p-1 transition-transform hover:scale-110 focus:outline-none"
                data-testid={`rating-star-${n}`}
                aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
              >
                <Star
                  size={44}
                  weight={active ? "fill" : "regular"}
                  className={active ? "text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]" : "text-white/25"}
                />
              </button>
            );
          })}
        </div>
        {rating > 0 && (
          <div className="text-center text-sm text-white/65">
            {["", "Très déçu", "Insuffisant", "Correct", "Très bien", "Excellent"][rating]}
          </div>
        )}

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Un mot pour l'équipe ? (facultatif)"
          rows={3}
          className="w-full rounded-xl bg-white/5 border border-white/10 p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#005CFF] resize-none"
          data-testid="rating-comment"
        />

        <button
          type="button"
          onClick={submit}
          disabled={rating < 1 || submitting}
          className="w-full h-12 rounded-xl bg-[#005CFF] hover:bg-[#0047CC] disabled:bg-white/10 disabled:text-white/40 text-white font-semibold transition-colors"
          data-testid="rating-submit"
        >
          {submitting ? "Envoi…" : (already ? "Mettre à jour" : "Envoyer ma note")}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#001633] text-white p-4 flex items-center justify-center">
      <div className="w-full max-w-md space-y-4">
        <div className="flex items-center justify-center gap-2 py-4">
          <div className="w-9 h-9 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)]">
            <span className="text-white font-bold">BK</span>
          </div>
          <div className="font-display font-bold text-lg">BKPRO Électricité</div>
        </div>
        {children}
        <div className="text-center text-[10px] text-white/30 py-4">Envoyé automatiquement par BKflow · Merci de votre confiance.</div>
      </div>
    </div>
  );
}
